import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import "./HomePage.css"; // reuse the animated text style

export default function TermsPage() {
  return (
    <Box marginTop={4}>
      <Stack
        justifyContent="center"
        alignItems="center"
        gap={{ xs: 3, md: 5 }}
        paddingX={0}
      >
        {/* Animated main heading */}
        <Typography
          className="animate-character"
          variant={"h3"}
          fontWeight="bold"
          align="center"
          sx={{
            "@media (max-width: 600px)": {
              fontSize: "2.2rem",
              letterSpacing: 0.5,
            },
            "@media (min-width: 601px) and (max-width: 900px)": {
              fontSize: "3.2rem",
              letterSpacing: 0.5,
            },
            "@media (min-width: 901px)": {
              fontSize: "4rem",
              letterSpacing: 0.5,
            },
          }}
        >
          Terms & Conditions
        </Typography>

        {/* Decorative dividers */}
        <Box sx={{ width: "100%" }}>
          <Divider />
          <Divider />
        </Box>

        {/* Content container */}
        <Box
          sx={{
            width: { xs: "95%", md: "80%" },
            maxWidth: "900px",
            textAlign: "left",
          }}
        >
          {/* Introduction */}
          <Divider sx={{ width: "100%", mb: 2, mt: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              Acceptance of Terms
            </Typography>
          </Divider>
          <Typography paragraph>
            By accessing or using SainiAudiobooks (the “Service”), you agree to
            be bound by these Terms & Conditions. If you do not agree to these
            terms, please do not use the Service.
          </Typography>

          {/* Account */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Account Registration
            </Typography>
          </Divider>
          <Typography paragraph>
            To use certain features, you must create an account. You agree to:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Provide accurate, current, and complete information.</li>
            <li>
              Maintain the security of your account credentials and notify us
              immediately of any unauthorised use.
            </li>
            <li>
              Be responsible for all activities that occur under your account.
            </li>
          </Typography>
          <Typography paragraph>
            We reserve the right to suspend or terminate accounts that violate
            these terms.
          </Typography>

          {/* Subscriptions */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Subscriptions & Purchases
            </Typography>
          </Divider>
          <Typography paragraph>
            SainiAudiobooks offers the following subscription plans (subject to
            change):
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>1 month access</li>
            <li>6 months access</li>
            <li>12 months access</li>
          </Typography>
          <Typography paragraph>
            Currently, subscriptions are offered as one‑time purchases. After
            the subscription period ends, access to premium content will cease
            unless renewed.
          </Typography>
          <Typography paragraph>
            All prices are displayed in Indian Rupees (INR) and are inclusive
            of applicable taxes.
          </Typography>

          {/* Payments */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Payments & Refunds
            </Typography>
          </Divider>
          <Typography paragraph>
            Payments are processed securely through Razorpay. By making a
            purchase, you confirm that you are authorised to use the payment
            method provided.
          </Typography>
          <Typography paragraph>
            <strong>All payments are final.</strong> Refunds will only be
            issued if a technical issue prevents you from accessing the
            purchased content. In such cases, please contact us within 7 days
            of purchase.
          </Typography>
          <Typography paragraph>
            We do not store your payment card details – they are handled
            directly by Razorpay in compliance with PCI‑DSS standards.
          </Typography>

          {/* Content Usage */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Content Usage & License
            </Typography>
          </Divider>
          <Typography paragraph>
            All audiobooks, recordings, images, and other materials on the
            Service are the property of SainiAudiobooks or its licensors and
            are protected by copyright laws.
          </Typography>
          <Typography paragraph>
            When you purchase a subscription, you are granted a non‑exclusive,
            non‑transferable, revocable license to access and stream the
            content for personal, non‑commercial use only. You may not:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Copy, record, or download the content.</li>
            <li>Distribute, resell, or publicly perform the content.</li>
            <li>Use the Service for any commercial purpose.</li>
          </Typography>
          <Typography paragraph>
            Any unauthorised use may result in immediate termination of your
            account and legal action.
          </Typography>

          {/* Termination */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Termination
            </Typography>
          </Divider>
          <Typography paragraph>
            We reserve the right to suspend or terminate your account and
            access to the Service at our sole discretion, without notice or
            liability, for any reason, including but not limited to:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Violation of these Terms & Conditions.</li>
            <li>Fraudulent or illegal activity.</li>
            <li>Misuse of the Service.</li>
          </Typography>
          <Typography paragraph>
            Upon termination, your right to use the Service will cease
            immediately, and any purchased content may become inaccessible.
          </Typography>

          {/* Disclaimer & Limitation of Liability */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Disclaimer & Limitation of Liability
            </Typography>
          </Divider>
          <Typography paragraph>
            The Service is provided “as is” and “as available” without
            warranties of any kind, either express or implied. We do not
            guarantee that the Service will be uninterrupted, error‑free, or
            free from viruses or other harmful components.
          </Typography>
          <Typography paragraph>
            To the maximum extent permitted by law, SainiAudiobooks shall not
            be liable for any indirect, incidental, special, consequential, or
            punitive damages, or any loss of profits or revenues, whether
            incurred directly or indirectly, or any loss of data, use,
            goodwill, or other intangible losses, resulting from:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>Your use or inability to use the Service.</li>
            <li>Any unauthorised access to or use of our servers.</li>
            <li>Any interruption or cessation of transmission to or from the Service.</li>
          </Typography>

          {/* Governing Law */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Governing Law & Dispute Resolution
            </Typography>
          </Divider>
          <Typography paragraph>
            These Terms & Conditions shall be governed by and construed in
            accordance with the laws of India, without regard to its conflict
            of law provisions. Any disputes arising out of or relating to these
            terms shall be subject to the exclusive jurisdiction of the courts
            in <strong>Ludhiana, Punjab]</strong>.
          </Typography>

          {/* Changes to Terms */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Changes to Terms
            </Typography>
          </Divider>
          <Typography paragraph>
            We may update these Terms & Conditions from time to time. Any
            changes will be posted on this page with an updated “Last Revised”
            date. Your continued use of the Service after such changes
            constitutes your acceptance of the new terms.
          </Typography>
          <Typography paragraph>
            <em>Last Revised: 28 March 2026</em>
          </Typography>

          {/* Contact */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Contact Us
            </Typography>
          </Divider>
          <Typography paragraph>
            If you have any questions about these Terms & Conditions, please
            contact us at:
            <br />
            <strong>Email:</strong> info@sainiaudiobooks.com
          </Typography>
        </Box>

        {/* Back to Home button */}
        <Button
          size="large"
          variant="contained"
          color="primary"
          sx={{
            px: 4,
            py: 1.75,
            fontSize: "1.15rem",
            fontWeight: 700,
            letterSpacing: 0.5,
            borderRadius: 9999,
            background: "linear-gradient(90deg, #0072ff 0%, #00c6ff 100%)",
            boxShadow:
              "0 6px 18px rgba(0,114,255,0.3), 0 2px 6px rgba(0,0,0,0.1)",
            textTransform: "none",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow:
                "0 10px 24px rgba(0,114,255,0.4), 0 4px 10px rgba(0,0,0,0.12)",
              background:
                "linear-gradient(90deg, #0062e6 0%, #00b6ff 100%) !important",
            },
            "&:focus-visible": {
              outline: "3px solid #111",
              outlineOffset: "3px",
            },
          }}
          component={Link}
          to="/"
          aria-label="Return to homepage"
        >
          ← Return to Home
        </Button>

        {/* Bottom decorative dividers */}
        <Box sx={{ width: "100%", mt: 4 }}>
          <Divider />
          <Divider />
        </Box>
      </Stack>
    </Box>
  );
}